import { useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import LogoSwipRent from "@/components/brand/logo_swiprent";
import InputField from "@/components/forms/input_field";
import ButtonMain from "@/components/buttons/button_main";

// Same strength floor as signup (8+ chars), plus a confirm field.
const schema = z
  .object({
    new_password: z.string().min(8, "Password must be at least 8 characters").max(128),
    confirm: z.string().min(1, "Confirm your new password"),
  })
  .refine((v) => v.new_password === v.confirm, {
    path: ["confirm"],
    message: "Passwords do not match",
  });

// Complete a password reset from the emailed link (?token=...). Validates the
// token server-side; an invalid or expired token shows a clear, on-brand error.
export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema) });

  async function onSubmit(values) {
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: values.new_password,
      });
      toast.success("Password reset. Please log in with your new password.");
      navigate("/auth/login", { replace: true });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const message =
        (detail && typeof detail === "object" && detail.message) ||
        (typeof detail === "string" ? detail : null) ||
        "This reset link is invalid or has expired. Please request a new one.";
      toast.warning(message);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-day px-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="w-full max-w-md rounded-3xl border border-slate-100 bg-white p-8 shadow-sm"
      >
        <Link to="/" className="inline-block">
          <LogoSwipRent />
        </Link>

        {!token ? (
          <div className="mt-6">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-tint text-brand-lime">
              <ShieldAlert size={22} />
            </span>
            <h1 className="mt-5 text-2xl font-extrabold tracking-tight text-brand-ink">Link not valid</h1>
            <p className="mt-2 text-sm font-semibold text-slate-500">
              This reset link is missing its token. Please request a new one.
            </p>
            <Link
              to="/auth/forgot-password"
              className="mt-7 inline-flex text-sm font-extrabold text-brand-lime transition hover:text-brand-lime"
            >
              Request a new link
            </Link>
          </div>
        ) : (
          <>
            <h1 className="mt-6 text-2xl font-extrabold tracking-tight text-brand-ink">Set a new password</h1>
            <p className="mt-1 text-sm font-semibold text-slate-500">
              Choose a strong password you have not used before.
            </p>

            <form onSubmit={handleSubmit(onSubmit)} className="mt-7 space-y-4">
              <InputField
                label="New password"
                type="password"
                placeholder="••••••••"
                error={errors.new_password?.message}
                {...register("new_password")}
              />
              <InputField
                label="Confirm new password"
                type="password"
                placeholder="••••••••"
                error={errors.confirm?.message}
                {...register("confirm")}
              />
              <ButtonMain type="submit" full disabled={isSubmitting}>
                {isSubmitting ? "Saving" : "Reset password"}
              </ButtonMain>
            </form>

            <p className="mt-6 text-center text-sm font-semibold text-slate-500">
              <Link to="/auth/login" className="font-extrabold text-brand-lime transition hover:text-brand-lime">
                Back to log in
              </Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
